import type { ComponentPropsWithRef, PropsWithChildren } from 'react'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { useId, useState } from 'react'
import { escapeCssIdent } from './escapeCssIdent'
import { Menu } from './Menu'
import styles from './SubMenu.module.css'
import { MenuTrigger } from './Trigger'

function SubMenuList({ ref, className, ...props }: ComponentPropsWithRef<typeof NavigationMenu.List>) {
  return <NavigationMenu.List ref={ref} className={cn(styles.list, className)} {...props} />
}
SubMenuList.displayName = 'SubMenu.List'

SubMenu.List = SubMenuList
SubMenu.Item = Menu.Item

export interface SubMenuProps extends NavigationMenu.NavigationMenuSubProps {
  label: string
}

export function SubMenu({ children, label }: PropsWithChildren<SubMenuProps>) {
  const [valueMenu, setValueMenu] = useState('')
  // See Menu.tsx: every SubMenu.Item renders its own <SubMenu>, all force-mounted at once for
  // SEO, so multiple instances share the same CSS Modules class name - scope the injected rule
  // to this instance's own viewport via a unique id.
  const viewportId = useId()

  return (
    <>
      <MenuTrigger onPointerMove={(e: React.PointerEvent) => e.preventDefault()}>{label}</MenuTrigger>
      <NavigationMenu.Content className={styles.wrapper} forceMount>
        <NavigationMenu.Sub className={styles.subMenu} orientation="vertical" value={valueMenu} onValueChange={setValueMenu}>
          <div className={styles.sidebar}>
            <Typography variant="body-m-bold-snug">
              {label}
            </Typography>
            <SubMenuList>{children}</SubMenuList>
          </div>
          <div className={styles.activeContent}>
            {/* See Menu.tsx for why we hide inactive Content nodes via a plain CSS attribute
                selector instead of imperatively toggling styles. */}
            <style dangerouslySetInnerHTML={{ __html: `#${escapeCssIdent(viewportId)} > :not([id$="${escapeCssIdent(valueMenu)}"]) { display: none; }` }} />
            <NavigationMenu.Viewport id={viewportId} className={styles.viewport} />
          </div>
        </NavigationMenu.Sub>
      </NavigationMenu.Content>
    </>
  )
}
